import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, Save } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import ContactCenterAccess from '../components/ContactCenter/ContactCenterAccess';
import { btn, card } from '../theme';

const ROLES = ['super_admin', 'admin', 'finance_reviewer', 'reviewer', 'read_only'];

const ROLE_COLORS = {
  super_admin:      { bg: '#EFF6FF', color: '#1D4ED8' },
  admin:            { bg: '#F0FDF4', color: '#16A34A' },
  finance_reviewer: { bg: '#FEF3C7', color: '#92400E' },
  reviewer:         { bg: '#FEF9C3', color: '#A16207' },
  read_only:        { bg: '#F3F4F6', color: '#6B7280' },
};

// Roles that grant access to every cost center automatically — per-CC
// assignments are unused and hidden from the editor.
const ROLES_BYPASS_CC = new Set(['super_admin', 'admin', 'finance_reviewer']);

export default function UserManagement() {
  const qc = useQueryClient();

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn:  () => api.get('/admin/users'),
  });

  const { data: costCenters = [], isLoading: ccLoading } = useQuery({
    queryKey: ['cost-centers'],
    queryFn:  () => api.get('/cost-centers'),
  });

  const { data: overtimeCostCenters = [], isLoading: occLoading } = useQuery({
    queryKey: ['admin-overtime-cost-centers'],
    queryFn:  () => api.get('/admin/overtime-cost-centers'),
  });

  if (usersLoading || ccLoading || occLoading) return <LoadingSpinner />;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Admin"
        title="User Management"
        subtitle="Manage roles and cost center access for all portal users. Users appear here after their first sign-in."
      />

      <div style={s.card}>
        {users.map((user, idx) => (
          <UserRow
            key={user.id}
            user={user}
            costCenters={costCenters}
            overtimeCostCenters={overtimeCostCenters}
            isLast={idx === users.length - 1}
            onUpdated={() => qc.invalidateQueries(['admin-users'])}
          />
        ))}
        {!users.length && (
          <div style={s.empty}>No users yet. Users appear here after their first sign-in.</div>
        )}
      </div>
    </div>
  );
}

// "Never" is deliberately distinct from "a long time ago": logging only began
// with migration 035, so an account with no row may simply predate it rather
// than be unused. Saying "no sign-in recorded" instead of "never" keeps that
// distinction, because the two lead to opposite decisions about an account.
function formatLastSeen(iso) {
  if (!iso) return 'No sign-in recorded';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'Seen today';
  if (days === 1) return 'Seen yesterday';
  if (days < 30)  return `Seen ${days} days ago`;
  if (days < 365) return `Seen ${Math.floor(days / 30)} months ago`;
  return `Seen ${Math.floor(days / 365)}y ago`;
}

function UserRow({ user, costCenters, overtimeCostCenters, isLast, onUpdated }) {
  const { user: currentUser, inventoryViewIsUniversal } = useAuth();
  const isSelf = currentUser?.id === user.id;
  const [expanded, setExpanded]   = useState(false);
  const [role, setRole]           = useState(user.role);

  const [assignments, setAssignments] = useState(() => {
    const map = {};
    (user.cost_centers || []).forEach((cc) => {
      map[cc.id] = { assigned: true, can_comment: cc.can_comment, can_submit: cc.can_submit };
    });
    return map;
  });

  const [otAssignments, setOtAssignments] = useState(() => {
    const map = {};
    (user.overtime_cost_centers || []).forEach((cc) => {
      map[cc.id] = { assigned: true, can_view_employees: cc.can_view_employees };
    });
    return map;
  });

  const [otFilter, setOtFilter] = useState('');

  // Inventory's grants, held separately from otAssignments on purpose. Merging
  // the two pieces of state is how the two lists would drift back into one.
  const [invAssignments, setInvAssignments] = useState(() => {
    const map = {};
    (user.inventory_cost_centers || []).forEach((cc) => { map[cc.id] = true; });
    return map;
  });
  const [invFilter, setInvFilter] = useState('');

  // Sales Report's grants (migration 052) -- a third list, kept apart from the
  // two above for the same reason they are kept apart from each other.
  const [salesAssignments, setSalesAssignments] = useState(() => {
    const map = {};
    (user.sales_cost_centers || []).forEach((cc) => { map[cc.id] = true; });
    return map;
  });
  const [salesFilter, setSalesFilter] = useState('');

  // R&M Dashboard's grants (migration 108), a list of its own like the two above.
  const [fleetAssignments, setFleetAssignments] = useState(() => {
    const map = {};
    (user.fleet_cost_centers || []).forEach((cc) => { map[cc.id] = true; });
    return map;
  });
  const [fleetFilter, setFleetFilter] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/role`, { role });

      // MOR cost center assignments
      const assignmentList = ROLES_BYPASS_CC.has(role)
        ? []
        : Object.entries(assignments)
            .filter(([, v]) => v.assigned)
            .map(([ccId, v]) => ({
              cost_center_id: ccId,
              can_comment:    role === 'read_only' ? false : v.can_comment,
              can_submit:     role === 'read_only' ? false : v.can_submit,
            }));
      await api.put(`/admin/users/${user.id}/cost-centers`, { assignments: assignmentList });

      // Overtime cost center assignments
      const otAssignmentList = ROLES_BYPASS_CC.has(role)
        ? []
        : Object.entries(otAssignments)
            .filter(([, v]) => v.assigned)
            .map(([ccId, v]) => ({
              overtime_cost_center_id: ccId,
              can_view_employees:      v.can_view_employees ?? true,
            }));
      await api.put(`/admin/users/${user.id}/overtime-cost-centers`, { assignments: otAssignmentList });

      // Inventory cost center assignments — its own request to its own endpoint.
      // Since migration 048 these are independent of the overtime list above;
      // sending one payload for both is exactly the coupling that was removed.
      const invAssignmentList = ROLES_BYPASS_CC.has(role)
        ? []
        : Object.keys(invAssignments)
            .filter((ccId) => invAssignments[ccId])
            .map((ccId) => ({ cost_center_id: ccId }));
      await api.put(`/admin/users/${user.id}/inventory-cost-centers`, { assignments: invAssignmentList });

      // Sales Report cost center assignments — likewise its own endpoint.
      const salesAssignmentList = ROLES_BYPASS_CC.has(role)
        ? []
        : Object.keys(salesAssignments)
            .filter((ccId) => salesAssignments[ccId])
            .map((ccId) => ({ cost_center_id: ccId }));
      await api.put(`/admin/users/${user.id}/sales-cost-centers`, { assignments: salesAssignmentList });

      // R&M Dashboard cost center assignments — likewise its own endpoint.
      const fleetAssignmentList = ROLES_BYPASS_CC.has(role)
        ? []
        : Object.keys(fleetAssignments)
            .filter((ccId) => fleetAssignments[ccId])
            .map((ccId) => ({ cost_center_id: ccId }));
      await api.put(`/admin/users/${user.id}/fleet-cost-centers`, { assignments: fleetAssignmentList });

      toast.success(`${user.display_name} updated successfully.`);
      onUpdated();
      setExpanded(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  function toggleCC(ccId) {
    setAssignments((prev) => ({
      ...prev,
      [ccId]: {
        assigned:    !prev[ccId]?.assigned,
        can_comment: prev[ccId]?.can_comment ?? true,
        can_submit:  prev[ccId]?.can_submit  ?? false,
      },
    }));
  }

  function togglePerm(ccId, perm) {
    setAssignments((prev) => ({
      ...prev,
      [ccId]: { ...prev[ccId], [perm]: !prev[ccId]?.[perm] },
    }));
  }

  function selectAllCCs() {
    setAssignments((prev) => {
      const next = { ...prev };
      for (const cc of costCenters) {
        next[cc.id] = { assigned: true, can_comment: prev[cc.id]?.can_comment ?? true, can_submit: prev[cc.id]?.can_submit ?? false };
      }
      return next;
    });
  }

  function clearAllCCs() {
    setAssignments((prev) => {
      const next = { ...prev };
      for (const cc of costCenters) next[cc.id] = { ...prev[cc.id], assigned: false };
      return next;
    });
  }

  function toggleOtCC(ccId) {
    setOtAssignments((prev) => ({
      ...prev,
      [ccId]: { assigned: !prev[ccId]?.assigned, can_view_employees: prev[ccId]?.can_view_employees ?? true },
    }));
  }

  function toggleOtPerm(ccId) {
    setOtAssignments((prev) => ({
      ...prev,
      [ccId]: { ...prev[ccId], can_view_employees: !prev[ccId]?.can_view_employees },
    }));
  }

  function selectAllOtCCs() {
    setOtAssignments((prev) => {
      const next = { ...prev };
      for (const cc of overtimeCostCenters) {
        next[cc.id] = { assigned: true, can_view_employees: prev[cc.id]?.can_view_employees ?? true };
      }
      return next;
    });
  }

  function clearAllOtCCs() {
    setOtAssignments((prev) => {
      const next = { ...prev };
      for (const cc of overtimeCostCenters) next[cc.id] = { ...prev[cc.id], assigned: false };
      return next;
    });
  }

  function toggleFleetCC(ccId) {
    setFleetAssignments((prev) => ({ ...prev, [ccId]: !prev[ccId] }));
  }

  function toggleInvCC(ccId) {
    setInvAssignments((prev) => ({ ...prev, [ccId]: !prev[ccId] }));
  }

  function selectAllInvCCs() {
    setInvAssignments(() => {
      const next = {};
      for (const cc of overtimeCostCenters) next[cc.id] = true;
      return next;
    });
  }

  function clearAllInvCCs() {
    setInvAssignments({});
  }

  function toggleSalesCC(ccId) {
    setSalesAssignments((prev) => ({ ...prev, [ccId]: !prev[ccId] }));
  }

  const activeMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/active`, { is_active: !user.is_active }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.is_active ? 'disabled' : 'enabled'}.`);
      onUpdated();
      setExpanded(false);
    },
    onError: (err) => toast.error(err.message || 'Failed to update active status.'),
  });

  // Standalone permission — independent of role — so it saves immediately
  // rather than being bundled into the Role / cost center "Save Changes" flow.
  const fuelCardAdminMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/fuel-card-admin`, { can_manage_fuel_cards: !user.can_manage_fuel_cards }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_manage_fuel_cards ? 'removed from' : 'granted'} Fuel Card Mapping admin access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Fuel Card Mapping admin access.'),
  });

  const timeWageAuditAdminMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/time-wage-audit-admin`, { can_review_time_wage_audit: !user.can_review_time_wage_audit }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_review_time_wage_audit ? 'removed from' : 'granted'} Time and Wage Audit access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Time and Wage Audit access.'),
  });

  const expenseAnalysisMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/expense-analysis-access`, { can_view_expense_analysis: !user.can_view_expense_analysis }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_expense_analysis ? 'removed from' : 'granted'} Expense Analysis access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Expense Analysis access.'),
  });

  const customerBillingMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/customer-billing-access`, { can_view_customer_billing: !user.can_view_customer_billing }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_customer_billing ? 'removed from' : 'granted'} Customer Billing access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Customer Billing access.'),
  });

  const postCollectionsMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/post-collections-access`, { can_view_post_collections: !user.can_view_post_collections }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_post_collections ? 'removed from' : 'granted'} Post Collections access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Post Collections access.'),
  });

  const softpakReportsMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/softpak-reports-access`, { can_view_softpak_reports: !user.can_view_softpak_reports }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_softpak_reports ? 'removed from' : 'granted'} Soft-Pak Reports access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Soft-Pak Reports access.'),
  });

  const scalePakMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/scale-pak-access`, { can_view_scale_pak: !user.can_view_scale_pak }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_scale_pak ? 'removed from' : 'granted'} Scale-Pak access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Scale-Pak access.'),
  });

  const disposalMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/disposal-access`, { can_view_disposal: !user.can_view_disposal }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_disposal ? 'removed from' : 'granted'} Disposal access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Disposal access.'),
  });

  const fleetReportsMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/fleet-reports-access`, { can_view_fleet_reports: !user.can_view_fleet_reports }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_fleet_reports ? 'removed from' : 'granted'} Fleet Reports.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Fleet Reports access.'),
  });

  const priceSheetsManageMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/price-sheets-manage`, { can_manage_price_sheets: !user.can_manage_price_sheets }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_manage_price_sheets ? 'removed from' : 'granted'} City Pricing edit access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update City Pricing edit access.'),
  });

  const priceSheetsViewMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/price-sheets-view`, { can_view_price_sheets: !user.can_view_price_sheets }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_price_sheets ? 'removed from' : 'granted'} City Pricing view access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update City Pricing view access.'),
  });

  const staffingTrackerAdminMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/staffing-tracker-admin`, { can_view_staffing_tracker: !user.can_view_staffing_tracker }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_staffing_tracker ? 'removed from' : 'granted'} unrestricted Staffing Tracker access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Staffing Tracker access.'),
  });

  // Fuel Report and Inventory used to ride on the Overtime cost-center
  // assignment. These grant each independently, all cost centers, view only.
  const fuelReportAccessMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/fuel-report-access`, { can_view_fuel_report: !user.can_view_fuel_report }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_fuel_report ? 'removed from' : 'granted'} Fuel Report access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Fuel Report access.'),
  });

  const rmDashboardAccessMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/rm-dashboard-access`, { can_view_rm_dashboard: !user.can_view_rm_dashboard }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_rm_dashboard ? 'removed from' : 'granted'} the R&M Dashboard for every cost center.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update R&M Dashboard access.'),
  });

  const mechanicDetailMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/mechanic-detail-access`, { can_view_mechanic_detail: !user.can_view_mechanic_detail }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_mechanic_detail ? 'can no longer see' : 'can now see'} mechanics by name.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update mechanic detail access.'),
  });

  const inventoryAccessMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/inventory-access`, { can_view_inventory: !user.can_view_inventory }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_inventory ? 'removed from' : 'granted'} Inventory access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Inventory access.'),
  });

  const salesReportAccessMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/sales-report-access`, { can_view_sales_report: !user.can_view_sales_report }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_sales_report ? 'removed from' : 'granted'} Sales Report access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update Sales Report access.'),
  });

  const hrHeadcountAccessMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/hr-headcount-access`, { can_view_hr_headcount: !user.can_view_hr_headcount }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_hr_headcount ? 'removed from' : 'granted'} unrestricted HR Headcount access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update HR Headcount access.'),
  });

  const hrAllocationsAccessMutation = useMutation({
    mutationFn: () =>
      api.patch(`/admin/users/${user.id}/hr-allocations-access`, { can_view_hr_allocations: !user.can_view_hr_allocations }),
    onSuccess: () => {
      toast.success(`${user.display_name} ${user.can_view_hr_allocations ? 'removed from' : 'granted'} HR Allocation access.`);
      onUpdated();
    },
    onError: (err) => toast.error(err.message || 'Failed to update HR Allocation access.'),
  });

  const rc = ROLE_COLORS[role] || ROLE_COLORS.read_only;
  const assignedCount    = Object.values(assignments).filter((v) => v.assigned).length;
  const otAssignedCount  = Object.values(otAssignments).filter((v) => v.assigned).length;

  const invAssignedCount = Object.values(invAssignments).filter(Boolean).length;
  const filteredInvCCs = invFilter.trim()
    ? overtimeCostCenters.filter((cc) =>
        `${cc.code} ${cc.name}`.toLowerCase().includes(invFilter.toLowerCase()))
    : overtimeCostCenters;

  const fleetAssignedCount = Object.values(fleetAssignments).filter(Boolean).length;
  const filteredFleetCCs = fleetFilter.trim()
    ? overtimeCostCenters.filter((cc) =>
        `${cc.code} ${cc.name}`.toLowerCase().includes(fleetFilter.toLowerCase()))
    : overtimeCostCenters;

  const salesAssignedCount = Object.values(salesAssignments).filter(Boolean).length;
  const filteredSalesCCs = salesFilter.trim()
    ? overtimeCostCenters.filter((cc) =>
        `${cc.code} ${cc.name}`.toLowerCase().includes(salesFilter.toLowerCase()))
    : overtimeCostCenters;

  const filteredOtCCs = otFilter.trim()
    ? overtimeCostCenters.filter((cc) =>
        `${cc.code} ${cc.name}`.toLowerCase().includes(otFilter.toLowerCase()))
    : overtimeCostCenters;

  return (
    <div style={{ ...s.row, borderBottom: isLast ? 'none' : '1px solid #F3F4F6', opacity: user.is_active ? 1 : 0.6 }}>
      {/* ── Summary row ── */}
      <div style={s.rowTop} onClick={() => setExpanded(!expanded)}>
        <div style={s.userInfo}>
          <div style={s.avatar}>{user.display_name?.charAt(0)?.toUpperCase() || '?'}</div>
          <div>
            <div style={s.name}>{user.display_name}</div>
            <div style={s.email}>{user.email}</div>
          </div>
        </div>
        <div style={s.rowMeta}>
          {/* Last sign-in, so a dormant account is visible on the list rather
              than only findable by querying. Stale credentials linger because
              nobody can see which ones stopped being used — this is the whole
              reason users.last_login_at exists (migration 035). */}
          <span style={s.lastSeen} title={user.last_login_at || 'No sign-in recorded since access logging began'}>
            {formatLastSeen(user.last_login_at)}
          </span>
          {!user.is_active && <span style={s.disabledBadge}>Disabled</span>}
          <span style={{ ...s.roleBadge, background: rc.bg, color: rc.color }}>
            {role.replace('_', ' ')}
          </span>
          <span style={s.ccCount}>
            {ROLES_BYPASS_CC.has(role)
              ? 'All CCs (by role)'
              : assignedCount > 0 || otAssignedCount > 0
                ? `${assignedCount} MOR · ${otAssignedCount} OT`
                : 'None assigned'}
          </span>
          <span style={s.lastLogin}>
            {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
          </span>
          <span style={s.expandIcon}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </div>
      </div>

      {/* ── Expanded editor ── */}
      {expanded && (
        <div style={s.editor}>
          {/* Role selector */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Role</div>
            <div style={s.roleRow}>
              {ROLES.map((r) => {
                const rc2 = ROLE_COLORS[r];
                return (
                  <button
                    key={r}
                    style={{
                      ...s.roleBtn,
                      background:  role === r ? rc2.bg    : '#F9FAFB',
                      color:       role === r ? rc2.color : '#6B7280',
                      borderColor: role === r ? rc2.color : '#E5E7EB',
                      fontWeight:  role === r ? 600 : 400,
                    }}
                    onClick={() => setRole(r)}
                  >
                    {r.replace('_', ' ')}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Fuel Card Mapping admin (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Fuel Card Mapping Admin</div>
            <div style={s.bypassNote}>
              A standalone permission, separate from Role and every other setting on this page.
              It grants full access (view, add, edit, delete) to the Fuel Card Mapping table and nothing else —
              holding <strong>super admin</strong> does not grant it automatically, and granting it does not
              grant super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_manage_fuel_cards}
                onChange={() => fuelCardAdminMutation.mutate()}
                disabled={fuelCardAdminMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can manage Fuel Card Mapping
            </label>
          </div>

          {/* ── Time and Wage Audit admin (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Time and Wage Audit Access</div>
            <div style={s.bypassNote}>
              A standalone permission, separate from Role and every other setting on this page.
              It grants access to the meal break discrepancy review dashboard — HR/legal-exposure-adjacent
              data about named employees — and nothing else. Holding <strong>super admin</strong> does not
              grant it automatically, and granting it does not grant super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_review_time_wage_audit}
                onChange={() => timeWageAuditAdminMutation.mutate()}
                disabled={timeWageAuditAdminMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can review Time and Wage Audit
            </label>
          </div>

          {/* ── Expense Analysis access (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Expense Analysis Access</div>
            <div style={s.bypassNote}>
              A standalone permission, separate from Role and every other setting on this page.
              It grants access to the Expense Analysis dashboard — company-wide spend, not scoped to
              assigned cost centers — and nothing else. Holding <strong>super admin</strong> does not
              grant it automatically, and granting it does not grant super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_expense_analysis}
                onChange={() => expenseAnalysisMutation.mutate()}
                disabled={expenseAnalysisMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can view Expense Analysis
            </label>
          </div>

          {/* ── Customer Billing access (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Customer Billing Access</div>
            <div style={s.bypassNote}>
              A standalone permission, like Expense Analysis. It grants the Customer Billing page —
              every customer&apos;s monthly bill, company-wide — and creating and editing billing-change
              scenarios. It does not grant uploading the Soft-Pak export, which stays super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_customer_billing}
                onChange={() => customerBillingMutation.mutate()}
                disabled={customerBillingMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can view Customer Billing
            </label>
          </div>

          {/* ── Post Collections access (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Post Collections Access</div>
            <div style={s.bypassNote}>
              A standalone permission, like Expense Analysis. It grants the Daily Productivity Report —
              tons, bales, labor hours and line uptime for every MRF and transfer site, not scoped to
              assigned cost centers. It does not grant uploading the daily export, which stays super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_post_collections}
                onChange={() => postCollectionsMutation.mutate()}
                disabled={postCollectionsMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can view Post Collections
            </label>
          </div>

          {/* ── Soft-Pak Reports access (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Soft-Pak Reports Access</div>
            <div style={s.bypassNote}>
              A standalone permission, like Expense Analysis. It grants the rebuilt Crystal reports read live from the
              Soft-Pak replica, for every company — four of them list customer names, addresses and phone numbers.
              Read-only: nothing on these reports can change Soft-Pak.
              {' '}<strong>For now only super admins see them</strong> while the reports are checked against Crystal; this
              setting is kept but has no effect until that limit is lifted.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_softpak_reports}
                onChange={() => softpakReportsMutation.mutate()}
                disabled
                title="Super admins only for now"
                style={{ marginRight: 6 }}
              />
              Can view Soft-Pak Reports
            </label>
          </div>

          {/* ── Scale-Pak access (standalone permission, all or nothing) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Scale-Pak Access</div>
            <div style={s.bypassNote}>
              A standalone permission, like Expense Analysis, and separate from Soft-Pak Reports. It grants the Scale-Pak
              dashboard: scale tickets, tons and loads for every facility and cost center, all or nothing, including the
              ticket list with customer names and its Excel export. Read-only: nothing on it can change Soft-Pak.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_scale_pak}
                onChange={() => scalePakMutation.mutate()}
                disabled={scalePakMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can view Scale-Pak
            </label>
          </div>

          {/* ── Disposal access (standalone permission, all or nothing) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Disposal Access</div>
            <div style={s.bypassNote}>
              A standalone permission, separate from Soft-Pak Reports and Scale-Pak. It grants the Disposal dashboard:
              disposal tickets, overweight loads, ticket matching, landfill cost and idle containers for every yard, all or
              nothing, including driver codes, customer names and, for idle containers, customer phone numbers and emails,
              and their Excel exports. Read-only: nothing on it can change Soft-Pak.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_disposal}
                onChange={() => disposalMutation.mutate()}
                disabled={disposalMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can view Disposal
            </label>
          </div>

          {/* ── Fleet Reports access (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Fleet Reports Access</div>
            <div style={s.bypassNote}>
              A standalone permission, like Expense Analysis. It grants work order, purchase order, uptime and utilization reports read live from Fleetio data, for every yard.
              Read-only: nothing on these reports can change Fleetio.
              {' '}<strong>For now only super admins see them</strong> while the reports are checked against Tableau; this
              setting is kept but has no effect until that limit is lifted.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_fleet_reports}
                onChange={() => fleetReportsMutation.mutate()}
                disabled
                title="Super admins only for now"
                style={{ marginRight: 6 }}
              />
              Can view Fleet Reports
            </label>
          </div>

          {/* ── R&M Dashboard access ──
              The Overtime and Inventory shape: an unrestricted role or the
              all-cost-centers box sees every yard, otherwise the cost centers ticked
              here. Mechanic names are a separate box on purpose. */}
          <div style={s.section}>
            <div style={s.sectionLabel}>R&amp;M Dashboard Access</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '8px', lineHeight: 1.5 }}>
              Separate from Fleet Reports and every list above. A cost center ticked here shows this user its Fleetio
              yards, once those are mapped under Mappings, Fleet Yards. Parts is company-wide, so it shows only for
              every cost center. Read-only: nothing on the dashboard can change Fleetio.
            </div>
            <label style={{ ...s.permLabel, fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_rm_dashboard}
                onChange={() => rmDashboardAccessMutation.mutate()}
                disabled={rmDashboardAccessMutation.isPending || ROLES_BYPASS_CC.has(role)}
                style={{ marginRight: 6 }}
              />
              R&amp;M Dashboard — read-only, all cost centers
            </label>
            <label style={{ ...s.permLabel, marginTop: '6px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={role === 'super_admin' || !!user.can_view_mechanic_detail}
                onChange={() => mechanicDetailMutation.mutate()}
                disabled={mechanicDetailMutation.isPending || role === 'super_admin'}
                style={{ marginRight: 6 }}
              />
              Can see mechanics by name (hours, standards and come-backs; no pay)
            </label>
            <div style={{ marginTop: '10px' }}>
              {ROLES_BYPASS_CC.has(role) ? (
                <div style={s.bypassNote}>
                  The <strong>{role.replace('_', ' ')}</strong> role sees every cost center on the R&amp;M Dashboard automatically.
                </div>
              ) : user.can_view_rm_dashboard ? (
                <div style={s.bypassNote}>
                  <strong>All cost centers</strong> is ticked for this user. Untick it to grant specific ones instead.
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Filter by code or name…"
                    value={fleetFilter}
                    onChange={(e) => setFleetFilter(e.target.value)}
                    style={s.filterInput}
                  />
                  <div style={s.otCcList}>
                    {filteredFleetCCs.map((cc) => {
                      const on = !!fleetAssignments[cc.id];
                      return (
                        <div key={cc.id} style={{ ...s.otCcItem, background: on ? '#EFF6FF' : '#F9FAFB', borderColor: on ? '#93C5FD' : '#E5E7EB' }}>
                          <label style={s.ccLabel}>
                            <input type="checkbox" checked={on} onChange={() => toggleFleetCC(cc.id)} style={{ marginRight: 6 }} />
                            <span style={s.ccCode}>{cc.code}</span>
                            <span style={s.ccName}>{cc.name}</span>
                          </label>
                        </div>
                      );
                    })}
                    {!filteredFleetCCs.length && <div style={s.noResults}>No cost centers match "{fleetFilter}"</div>}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>
                    {fleetAssignedCount} cost center{fleetAssignedCount === 1 ? '' : 's'} assigned. Saved with the button below.
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── City Pricing access (two standalone permissions) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>City Pricing Access</div>
            <div style={s.bypassNote}>
              Two standalone permissions, separate from Role and everything else on this page, and
              independent of each other. <strong>Can manage</strong> grants full CRUD on contracts,
              bundles, and fees (finance); <strong>can view</strong> grants read-only access to the same
              data (e.g. customer service). Holding <strong>super admin</strong> does not grant either
              automatically, and granting either does not grant super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px', display: 'block' }}>
              <input
                type="checkbox"
                checked={!!user.can_manage_price_sheets}
                onChange={() => priceSheetsManageMutation.mutate()}
                disabled={priceSheetsManageMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can manage City Pricing (create/edit/delete)
            </label>
            <label style={{ ...s.permLabel, marginTop: '6px', fontSize: '12px', display: 'block' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_price_sheets}
                onChange={() => priceSheetsViewMutation.mutate()}
                disabled={priceSheetsViewMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Can view City Pricing (read-only)
            </label>
          </div>

          {/* ── Staffing Tracker admin (standalone permission) ── */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Staffing Tracker Access</div>
            <div style={s.bypassNote}>
              A standalone permission, separate from Role and every other setting on this page. It
              grants unrestricted (all-facility) access to the Staffing &amp; Development Tracker —
              for HR/Recruiting, who need to see every facility rather than just assigned ones.
              DVPs get facility-scoped access separately, via the cost center assignments below.
              Holding <strong>super admin</strong> does not grant it automatically, and granting it
              does not grant super admin.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_staffing_tracker}
                onChange={() => staffingTrackerAdminMutation.mutate()}
                disabled={staffingTrackerAdminMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Unrestricted Staffing Tracker access (HR/Recruiting)
            </label>

            <div style={{ ...s.sectionLabel, marginTop: '18px' }}>Fuel Report, Inventory &amp; Sales Report Access</div>
            <div style={s.bypassNote}>
              Granted separately from Overtime. Each
              gives read-only access to <strong>all cost centers</strong> on that page — no upload,
              no editing. Overtime keeps its own per-cost-center assignment above, and a user with
              Overtime cost centers still sees those pages scoped to their sites.
            </div>
            <label style={{ ...s.permLabel, marginTop: '8px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_fuel_report}
                onChange={() => fuelReportAccessMutation.mutate()}
                disabled={fuelReportAccessMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Fuel Report — read-only, all cost centers
            </label>
            {/* The grant still writes can_view_inventory, and that column is
                still the revert path — but while the dashboard is universal it
                decides nothing. Left visible and editable rather than removed:
                hiding it would lose the setting an admin has already made, and
                a checkbox that saves, toasts success and changes nothing is the
                trap this note exists to close. */}
            <label style={{
              ...s.permLabel, marginTop: '4px', fontSize: '12px',
              opacity: inventoryViewIsUniversal ? 0.6 : 1,
            }}>
              <input
                type="checkbox"
                checked={!!user.can_view_inventory}
                onChange={() => inventoryAccessMutation.mutate()}
                disabled={inventoryAccessMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Inventory — read-only, all cost centers
            </label>
            <label style={{ ...s.permLabel, marginTop: '4px', fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={!!user.can_view_sales_report}
                onChange={() => salesReportAccessMutation.mutate()}
                disabled={salesReportAccessMutation.isPending}
                style={{ marginRight: 6 }}
              />
              Sales Report — read-only, all cost centers
            </label>

            <label style={s.permLabel}>
              <input
                type="checkbox"
                checked={!!user.can_view_hr_headcount}
                onChange={() => hrHeadcountAccessMutation.mutate()}
                disabled={hrHeadcountAccessMutation.isPending}
                style={{ marginRight: 6 }}
              />
              HR Headcount — unrestricted, all cost centers
            </label>
            <div style={s.permNote}>
              Without this, the user still sees HR Headcount for whatever cost
              centers they are assigned above — this grant lifts that limit.
              Worker names remain governed by "can view employees" per cost center.
            </div>

            <label style={s.permLabel}>
              <input
                type="checkbox"
                checked={!!user.can_view_hr_allocations}
                onChange={() => hrAllocationsAccessMutation.mutate()}
                disabled={hrAllocationsAccessMutation.isPending}
                style={{ marginRight: 6 }}
              />
              HR Headcount Allocations — company-wide
            </label>
            <div style={s.permNote}>
              All or nothing: the allocation view is company-wide by design, so
              this is not narrowed by cost-center assignments.
            </div>
            {inventoryViewIsUniversal && (
              <div style={s.permNote}>
                No effect right now — the Inventory dashboard is visible to every
                signed-in user. This grant is kept for when the board is split by
                permission level again.
              </div>
            )}
          </div>

          {/* ── Contact Center (migration 053) ──
              Its own component and its own save: a role and its departments
              only mean something together, and the row's Save Changes below
              already sends five requests about other modules. */}
          <div style={s.section}>
            <div style={s.sectionLabel}>Contact Center Access</div>
            <ContactCenterAccess user={user} onUpdated={onUpdated} />
          </div>

          {/* ── MOR cost center assignments ── */}
          <div style={s.section}>
            <div style={s.sectionHeaderRow}>
              <div style={s.sectionLabel}>MOR Cost Center Access</div>
              {!ROLES_BYPASS_CC.has(role) && (
                <div style={s.bulkBtnRow}>
                  <button type="button" style={s.bulkBtn} onClick={selectAllCCs}>Select all</button>
                  <button type="button" style={s.bulkBtn} onClick={clearAllCCs}>Clear all</button>
                </div>
              )}
            </div>
            {ROLES_BYPASS_CC.has(role) ? (
              <div style={s.bypassNote}>
                The <strong>{role.replace('_', ' ')}</strong> role grants access to all MOR cost centers automatically.
              </div>
            ) : (
              <div style={s.ccGrid}>
                {costCenters.map((cc) => {
                  const a = assignments[cc.id] || { assigned: false, can_comment: true, can_submit: false };
                  return (
                    <div key={cc.id} style={{ ...s.ccItem, background: a.assigned ? '#F0FDF4' : '#F9FAFB', borderColor: a.assigned ? '#86EFAC' : '#E5E7EB' }}>
                      <div style={s.ccTop}>
                        <label style={s.ccLabel}>
                          <input type="checkbox" checked={!!a.assigned} onChange={() => toggleCC(cc.id)} style={{ marginRight: 6 }} />
                          <span style={s.ccCode}>{cc.code}</span>
                          <span style={s.ccName}>{cc.name}</span>
                        </label>
                      </div>
                      {a.assigned && role === 'read_only' && <div style={s.ccReadOnlyTag}>View only</div>}
                      {a.assigned && role !== 'read_only' && (
                        <div style={s.ccPerms}>
                          <label style={s.permLabel}>
                            <input type="checkbox" checked={!!a.can_comment} onChange={() => togglePerm(cc.id, 'can_comment')} style={{ marginRight: 4 }} />
                            Can comment
                          </label>
                          <label style={s.permLabel}>
                            <input type="checkbox" checked={!!a.can_submit} onChange={() => togglePerm(cc.id, 'can_submit')} style={{ marginRight: 4 }} />
                            Can submit
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Overtime cost center assignments ── */}
          <div style={s.section}>
            <div style={s.sectionHeaderRow}>
              <div style={s.sectionLabel}>Overtime, Fuel Report, HR Headcount &amp; Staffing Cost Center Access</div>
              {!ROLES_BYPASS_CC.has(role) && (
                <div style={s.bulkBtnRow}>
                  <button type="button" style={s.bulkBtn} onClick={selectAllOtCCs}>Select all</button>
                  <button type="button" style={s.bulkBtn} onClick={clearAllOtCCs}>Clear all</button>
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '8px', lineHeight: 1.5 }}>
              These assignments control the Overtime dashboard, the Fuel Report, HR Headcount and Staffing —
              those four still share one cost center list, so a facility granted here becomes visible in all
              of them. A user with none assigned here won't see any of the four in their sidebar.
              <strong> Inventory is no longer one of them</strong> — it has its own list below, and granting a
              facility here does not grant it there.
            </div>
            {ROLES_BYPASS_CC.has(role) ? (
              <div style={s.bypassNote}>
                The <strong>{role.replace('_', ' ')}</strong> role grants access to all overtime, fuel report,
                HR headcount and staffing cost centers automatically.
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Filter by code or name…"
                  value={otFilter}
                  onChange={(e) => setOtFilter(e.target.value)}
                  style={s.filterInput}
                />
                <div style={s.otCcList}>
                  {filteredOtCCs.map((cc) => {
                    const a = otAssignments[cc.id] || { assigned: false, can_view_employees: true };
                    return (
                      <div key={cc.id} style={{ ...s.otCcItem, background: a.assigned ? '#EFF6FF' : '#F9FAFB', borderColor: a.assigned ? '#93C5FD' : '#E5E7EB' }}>
                        <label style={s.ccLabel}>
                          <input type="checkbox" checked={!!a.assigned} onChange={() => toggleOtCC(cc.id)} style={{ marginRight: 6 }} />
                          <span style={s.ccCode}>{cc.code}</span>
                          <span style={s.ccName}>{cc.name}</span>
                        </label>
                        {a.assigned && (
                          <label style={{ ...s.permLabel, marginTop: 4, paddingLeft: 20 }}>
                            <input type="checkbox" checked={!!a.can_view_employees} onChange={() => toggleOtPerm(cc.id)} style={{ marginRight: 4 }} />
                            Can view employees
                          </label>
                        )}
                      </div>
                    );
                  })}
                  {!filteredOtCCs.length && <div style={s.noResults}>No cost centers match "{otFilter}"</div>}
                </div>
              </>
            )}
          </div>

          {/* ── Inventory cost center assignments ──
              A section of its own, not a column added to the list above. The
              two grants were one table until migration 048, and a shared UI is
              how they would become one again: an admin ticking a facility for
              payroll would tick it for parts without deciding to. */}
          <div style={s.section}>
            <div style={s.sectionHeaderRow}>
              <div style={s.sectionLabel}>Inventory Cost Center Access</div>
              {!ROLES_BYPASS_CC.has(role) && !user.can_view_inventory && (
                <div style={s.bulkBtnRow}>
                  <button type="button" style={s.bulkBtn} onClick={selectAllInvCCs}>Select all</button>
                  <button type="button" style={s.bulkBtn} onClick={clearAllInvCCs}>Clear all</button>
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '8px', lineHeight: 1.5 }}>
              Separate from the list above. A facility granted here shows this user that facility's parts and
              nothing else, and does not affect Overtime, Fuel, HR Headcount or Staffing. A user with none
              assigned here, no unrestricted role and no all-cost-centers grant sees an empty Inventory board.
            </div>
            {ROLES_BYPASS_CC.has(role) ? (
              <div style={s.bypassNote}>
                The <strong>{role.replace('_', ' ')}</strong> role sees every inventory cost center
                automatically.
              </div>
            ) : user.can_view_inventory ? (
              /* The all-cost-centers checkbox above already covers everything,
                 so per-facility ticks would be dead controls that still save. */
              <div style={s.bypassNote}>
                <strong>Inventory — read-only, all cost centers</strong> is ticked for this user, which covers
                every facility. Untick it above to grant specific ones instead.
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Filter by code or name…"
                  value={invFilter}
                  onChange={(e) => setInvFilter(e.target.value)}
                  style={s.filterInput}
                />
                <div style={s.otCcList}>
                  {filteredInvCCs.map((cc) => {
                    const on = !!invAssignments[cc.id];
                    return (
                      <div key={cc.id} style={{ ...s.otCcItem, background: on ? '#EFF6FF' : '#F9FAFB', borderColor: on ? '#93C5FD' : '#E5E7EB' }}>
                        <label style={s.ccLabel}>
                          <input type="checkbox" checked={on} onChange={() => toggleInvCC(cc.id)} style={{ marginRight: 6 }} />
                          <span style={s.ccCode}>{cc.code}</span>
                          <span style={s.ccName}>{cc.name}</span>
                        </label>
                      </div>
                    );
                  })}
                  {!filteredInvCCs.length && <div style={s.noResults}>No cost centers match "{invFilter}"</div>}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>
                  {invAssignedCount} facilit{invAssignedCount === 1 ? 'y' : 'ies'} assigned
                </div>
              </>
            )}
          </div>

          {/* ── Sales Report cost center assignments ──
              Its own section and its own list, for the reason the Inventory
              one above is: a yard's revenue per cubic yard is granted on
              purpose, never ticked in passing for payroll or parts. */}
          <div style={s.section}>
            <div style={s.sectionHeaderRow}>
              <div style={s.sectionLabel}>Sales Report Cost Center Access</div>
              {!ROLES_BYPASS_CC.has(role) && !user.can_view_sales_report && (
                <div style={s.bulkBtnRow}>
                  <button type="button" style={s.bulkBtn} onClick={() => setSalesAssignments({})}>Clear all</button>
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '8px', lineHeight: 1.5 }}>
              Separate from Overtime and Inventory. A cost center granted here shows this user that yard's Open
              Market sales and nothing else. Lines that resolve to no portal cost center are only ever visible
              to unrestricted access.
            </div>
            {ROLES_BYPASS_CC.has(role) ? (
              <div style={s.bypassNote}>
                The <strong>{role.replace('_', ' ')}</strong> role sees every sales cost center automatically.
              </div>
            ) : user.can_view_sales_report ? (
              <div style={s.bypassNote}>
                <strong>Sales Report — read-only, all cost centers</strong> is ticked for this user, which covers
                every cost center. Untick it above to grant specific ones instead.
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Filter by code or name…"
                  value={salesFilter}
                  onChange={(e) => setSalesFilter(e.target.value)}
                  style={s.filterInput}
                />
                <div style={s.otCcList}>
                  {filteredSalesCCs.map((cc) => {
                    const on = !!salesAssignments[cc.id];
                    return (
                      <div key={cc.id} style={{ ...s.otCcItem, background: on ? '#EFF6FF' : '#F9FAFB', borderColor: on ? '#93C5FD' : '#E5E7EB' }}>
                        <label style={s.ccLabel}>
                          <input type="checkbox" checked={on} onChange={() => toggleSalesCC(cc.id)} style={{ marginRight: 6 }} />
                          <span style={s.ccCode}>{cc.code}</span>
                          <span style={s.ccName}>{cc.name}</span>
                        </label>
                      </div>
                    );
                  })}
                  {!filteredSalesCCs.length && <div style={s.noResults}>No cost centers match "{salesFilter}"</div>}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>
                  {salesAssignedCount} cost center{salesAssignedCount === 1 ? '' : 's'} assigned
                </div>
              </>
            )}
          </div>

          {/* Action buttons */}
          <div style={s.saveRow}>
            {!isSelf && (
              <button
                style={s.warningBtn}
                onClick={() => activeMutation.mutate()}
                disabled={activeMutation.isPending}
                title={user.is_active ? 'Block this user from logging in' : 'Re-enable this user'}
              >
                {activeMutation.isPending ? 'Saving…' : user.is_active ? 'Disable Account' : 'Enable Account'}
              </button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button style={s.cancelBtn} onClick={() => setExpanded(false)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
                <Save size={13} />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:        { maxWidth: '900px', margin: '0 auto' },
  card:        { ...card.base, overflow: 'hidden' },
  row:         { padding: '0' },
  rowTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', cursor: 'pointer',
  },
  userInfo:    { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: {
    width: '34px', height: '34px', borderRadius: '50%', background: '#EFF6FF',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: 700, color: '#1D4ED8', flexShrink: 0,
  },
  name:        { fontSize: '13px', fontWeight: 600, color: '#111827' },
  email:       { fontSize: '11px', color: '#9CA3AF', marginTop: '1px' },
  rowMeta:     { display: 'flex', alignItems: 'center', gap: '12px' },
  roleBadge:   { fontSize: '11px', padding: '3px 10px', borderRadius: '12px', fontWeight: 500, textTransform: 'capitalize' },
  ccCount:     { fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' },
  lastLogin:   { fontSize: '11px', color: '#D1D5DB', whiteSpace: 'nowrap' },
  expandIcon:  { color: '#9CA3AF', display: 'flex' },
  editor: {
    padding: '16px 20px 20px',
    background: '#F9FAFB',
    borderTop: '1px solid #E5E7EB',
  },
  section:     { marginBottom: '20px' },
  sectionLabel:{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#6B7280', marginBottom: '8px' },
  roleRow:     { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  roleBtn: {
    padding: '6px 14px', fontSize: '12px', border: '1px solid',
    borderRadius: '20px', cursor: 'pointer', fontFamily: 'inherit',
    textTransform: 'capitalize', transition: 'all 0.15s',
  },
  ccGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' },
  ccItem:      { border: '1px solid', borderRadius: '8px', padding: '10px 12px', transition: 'all 0.15s' },
  ccTop:       { marginBottom: '4px' },
  ccLabel:     { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' },
  ccCode:      { fontWeight: 700, color: '#1B2A4A', fontSize: '11px' },
  ccName:      { color: '#374151' },
  ccPerms:     { display: 'flex', gap: '12px', marginTop: '6px', paddingLeft: '20px' },
  permLabel:   { display: 'flex', alignItems: 'center', fontSize: '11px', color: '#6B7280', cursor: 'pointer' },
  // Indented to the checkbox's label text, so it reads as a note on that row
  // rather than as the next permission in the list.
  permNote:    { marginLeft: '20px', marginTop: '2px', fontSize: '11px', lineHeight: 1.4, color: '#9CA3AF', maxWidth: '340px' },
  filterInput: {
    width: '100%', boxSizing: 'border-box', padding: '7px 12px', fontSize: '12px',
    border: '1px solid #D1D5DB', borderRadius: '6px', marginBottom: '8px',
    fontFamily: 'inherit', color: '#374151', outline: 'none',
  },
  otCcList:    { maxHeight: '320px', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px', paddingRight: '2px' },
  otCcItem:    { border: '1px solid', borderRadius: '8px', padding: '8px 10px', transition: 'all 0.15s' },
  noResults:   { fontSize: '12px', color: '#9CA3AF', padding: '12px', gridColumn: '1/-1', textAlign: 'center' },
  saveRow:     { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' },
  cancelBtn: {
    padding: '7px 14px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
  },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '7px 16px', fontSize: '12px', fontWeight: 600,
    border: 'none', borderRadius: '6px', background: '#1B2A4A',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  empty:       { textAlign: 'center', padding: '32px', color: '#9CA3AF', fontSize: '13px' },
  bypassNote:  { fontSize: '12px', color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', lineHeight: 1.6 },
  sectionHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  bulkBtnRow:  { display: 'flex', gap: '6px' },
  bulkBtn:     { ...btn.secondaryXs },
  ccReadOnlyTag: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', marginTop: '6px', paddingLeft: '20px', fontStyle: 'italic' },
  disabledBadge: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '2px 8px', borderRadius: '10px', background: '#FEE2E2', color: '#B91C1C' },
  lastSeen: {
    fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  warningBtn: { ...btn.warning },
};
